import React from 'react';
import { FilterButton, FilterDropdown } from '../../../ui';
import { CalendarOff, Clock, Filter, Star, Tag, UserX } from 'lucide-react';

// Update to allow tag IDs by using a string union type
type FilterOptionId = 'unscheduled' | 'unassigned' | 'highlighted' | 'sortByDeadline' | string;

/**
 * Task status filter dropdown component
 * Used to filter tasks by status (unscheduled/unassigned/highlighted), by tags, and for sorting
 * Displayed in the task topic filter toolbar
 */
interface TaskStatusFilterDropdownProps {
  showUnscheduledOnly: boolean;
  toggleShowUnscheduledOnly: () => void;
  showUnassignedOnly: boolean;
  toggleShowUnassignedOnly: () => void;
  showHighlightedOnly: boolean;
  toggleShowHighlightedOnly: () => void;
  isOpen: boolean;
  onToggle: () => void;
  isMobile: boolean;
  hasCollaborators?: boolean;
  // Tag filter properties
  availableTags?: string[];
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  // Sort options
  sortByDeadline?: boolean;
  toggleSortByDeadline?: () => void;
}

const TaskStatusFilterDropdown: React.FC<TaskStatusFilterDropdownProps> = ({
  showUnscheduledOnly,
  toggleShowUnscheduledOnly,
  showUnassignedOnly,
  toggleShowUnassignedOnly,
  showHighlightedOnly,
  toggleShowHighlightedOnly,
  isOpen,
  onToggle,
  isMobile,
  hasCollaborators = false,
  availableTags = [],
  selectedTags = [],
  onTagsChange = () => {},
  sortByDeadline = false,
  toggleSortByDeadline = () => {},
}) => {
  // Use only the explicitly passed isMobile prop
  const actualMobile = isMobile;

  // Keep track of selected values
  const selectedValues: FilterOptionId[] = [];
  if (showUnscheduledOnly) selectedValues.push('unscheduled');
  if (showUnassignedOnly) selectedValues.push('unassigned');
  if (showHighlightedOnly) selectedValues.push('highlighted');
  if (sortByDeadline) selectedValues.push('sortByDeadline');
  // Include selected tags
  selectedTags.forEach(tag => selectedValues.push(`tag:${tag}`));

  // Handle selection
  const handleChange = (newSelectedValues: FilterOptionId[]) => {
    // Separate tag filters from status filters
    const newTagFilters = newSelectedValues
      .filter(val => val.startsWith('tag:'))
      .map(val => val.replace('tag:', ''));

    // First handle tag filters - these can be multiple
    if (
      newTagFilters.length !== selectedTags.length ||
      !newTagFilters.every(tag => selectedTags.includes(tag))
    ) {
      onTagsChange(newTagFilters);
    }

    // Handle status filters - these are exclusive
    const newUnscheduled = newSelectedValues.includes('unscheduled');
    const newUnassigned = newSelectedValues.includes('unassigned');
    const newHighlighted = newSelectedValues.includes('highlighted');
    const newSortByDeadline = newSelectedValues.includes('sortByDeadline');

    // Update unscheduled filter if needed
    if (showUnscheduledOnly !== newUnscheduled) {
      toggleShowUnscheduledOnly();
    }

    // Update unassigned filter if needed
    if (showUnassignedOnly !== newUnassigned) {
      toggleShowUnassignedOnly();
    }

    // Update highlighted filter if needed
    if (showHighlightedOnly !== newHighlighted) {
      toggleShowHighlightedOnly();
    }

    // Update sort by deadline filter if needed
    if (sortByDeadline !== newSortByDeadline) {
      toggleSortByDeadline();
    }
  };

  // Handle clear - clear all filters
  const handleClear = () => {
    if (showUnscheduledOnly) toggleShowUnscheduledOnly();
    if (showUnassignedOnly) toggleShowUnassignedOnly();
    if (showHighlightedOnly) toggleShowHighlightedOnly();
    if (sortByDeadline) toggleSortByDeadline();
    if (selectedTags.length > 0) onTagsChange([]);
  };

  // Create sections for our filter options based on what's available with icons
  const filterOptions = [
    {
      id: 'highlighted' as FilterOptionId,
      label: 'Highlighted Tasks',
      icon: <Star size={16} className="text-blue-500" />,
    },
    {
      id: 'unscheduled' as FilterOptionId,
      label: 'Unscheduled Tasks',
      icon: <CalendarOff size={16} className="text-blue-500" />,
    },
  ];

  // Only add unassigned option if the project has collaborators
  if (hasCollaborators) {
    filterOptions.push({
      id: 'unassigned' as FilterOptionId,
      label: 'Unassigned Tasks',
      icon: <UserX size={16} className="text-blue-500" />,
    });
  }

  // Create tag filter options
  const tagOptions = availableTags.map(tag => ({
    id: `tag:${tag}` as FilterOptionId,
    label: tag,
    icon: <Tag size={16} className="text-blue-500" />,
  }));

  // Create a sort option
  const sortOptions = [
    {
      id: 'sortByDeadline' as FilterOptionId,
      label: 'By Deadline (earliest first)',
      icon: <Clock size={16} className="text-blue-500" />,
    },
  ];

  // Create sections for our dropdown
  const sections = [
    {
      title: 'Status',
      options: filterOptions,
    },
  ];

  // Only add tags section if we have available tags
  if (availableTags.length > 0) {
    sections.push({
      title: 'Tags',
      options: tagOptions,
    });
  }

  // Add a Sort section
  sections.push({
    title: 'Sort',
    options: sortOptions,
  });

  // Get button text based on selected filters
  const getButtonText = () => {
    if (selectedValues.length === 0) {
      return 'Filter';
    } else if (selectedValues.length === 1) {
      // Get specific label based on the selected filter type
      if (selectedValues[0] === 'unscheduled') {
        return 'Unscheduled only';
      } else if (selectedValues[0] === 'unassigned') {
        return 'Unassigned only';
      } else if (selectedValues[0] === 'highlighted') {
        return 'Highlighted only';
      } else if (selectedValues[0] === 'sortByDeadline') {
        return 'Sort: By Deadline';
      } else if (selectedValues[0].startsWith('tag:')) {
        return `Tag: ${selectedValues[0].replace('tag:', '')}`;
      }
      return 'Filtered';
    } else {
      return `Filters (${selectedValues.length})`;
    }
  };

  // Custom button to show in the dropdown using the reusable FilterButton
  const statusFilterButton = (
    <FilterButton
      isActive={isOpen}
      hasFilters={selectedValues.length > 0}
      onClick={onToggle}
      onClear={e => {
        e.stopPropagation();
        handleClear();
        onToggle();
      }}
      label={getButtonText()}
      icon={<Filter size={16} className="text-blue-500" />}
      isMobile={actualMobile}
      className=""
    />
  );

  return (
    <FilterDropdown
      sections={sections}
      selectedValues={selectedValues}
      onChange={handleChange}
      onClear={handleClear}
      allowMultiple={true}
      placeholder="Filter"
      headerText="Filter"
      isMobile={actualMobile}
      open={isOpen}
      onOpenChange={onToggle}
      showBackdrop={actualMobile}
      mobilePosition={0.2}
      trigger={statusFilterButton}
    />
  );
};

export default TaskStatusFilterDropdown;
